import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";

function AppealTable({ appeals, stage, onDecide, emptyText, showTeacherNote }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Student</TableHead>
          <TableHead>Roll No</TableHead>
          <TableHead>Class Date</TableHead>
          <TableHead>Period</TableHead>
          <TableHead>Subject</TableHead>
          <TableHead>Student's Reason</TableHead>
          {showTeacherNote && <TableHead>Teacher's Note</TableHead>}
          <TableHead className="w-44">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {appeals.length === 0 && (
          <TableRow>
            <TableCell colSpan={showTeacherNote ? 8 : 7} className="text-center text-slate-400 py-10"
              data-testid={`${stage}-appeals-empty`}>
              {emptyText}
            </TableCell>
          </TableRow>
        )}
        {appeals.map((a) => (
          <TableRow key={a.id} data-testid={`${stage}-appeal-row-${a.id}`}>
            <TableCell className="font-medium">{a.student_name}</TableCell>
            <TableCell className="font-data text-sm">{a.roll_number}</TableCell>
            <TableCell className="font-data">{a.date}</TableCell>
            <TableCell>{a.period_label}</TableCell>
            <TableCell>{a.subject_name}</TableCell>
            <TableCell className="text-sm text-slate-500 max-w-56">{a.reason}</TableCell>
            {showTeacherNote && (
              <TableCell className="text-sm text-slate-500 max-w-48">{a.teacher_decision?.reason || "—"}</TableCell>
            )}
            <TableCell>
              <div className="flex gap-2">
                <Button size="sm" data-testid={`${stage}-approve-btn-${a.id}`}
                  onClick={() => onDecide(a, "approve", stage)}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white">
                  <Check className="h-4 w-4" /> Approve
                </Button>
                <Button size="sm" variant="outline" data-testid={`${stage}-reject-btn-${a.id}`}
                  onClick={() => onDecide(a, "reject", stage)}
                  className="text-red-700 border-red-300 hover:bg-red-50">
                  <X className="h-4 w-4" /> Reject
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function TeacherAppeals() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [decision, setDecision] = useState(null); // {appeal, action, stage}
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: teacherAppeals = [] } = useQuery({
    queryKey: ["teacher-appeals"],
    queryFn: async () => (await api.get("/appeals/teacher")).data,
  });
  const { data: hodAppeals = [] } = useQuery({
    queryKey: ["hod-appeals"],
    queryFn: async () => (await api.get("/appeals/hod")).data,
    enabled: !!user?.is_hod,
  });

  const openDecide = (appeal, action, stage) => {
    setReason("");
    setDecision({ appeal, action, stage });
  };

  const submitDecision = async () => {
    if (!reason.trim()) {
      toast.error("A reason is required");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/appeals/${decision.stage}/${decision.appeal.id}/decide`,
        { action: decision.action, reason });
      toast.success(decision.action === "approve" ? "Appeal approved" : "Appeal rejected");
      setDecision(null);
      queryClient.invalidateQueries({ queryKey: ["teacher-appeals"] });
      queryClient.invalidateQueries({ queryKey: ["hod-appeals"] });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout title="Attendance Appeals">
      <div className="space-y-8">
        <div className="bg-white border border-slate-200 rounded-md" data-testid="teacher-appeals-card">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950">
              Appeals awaiting your review
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Students appealing absences in classes you marked. Approving forwards the appeal to the HOD for final approval.
            </p>
          </div>
          <AppealTable appeals={teacherAppeals} stage="teacher" onDecide={openDecide}
            emptyText="No pending appeals for your classes." />
        </div>

        {user?.is_hod && (
          <div className="bg-white border border-slate-200 rounded-md" data-testid="hod-appeals-card">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950">
                HOD final approvals — {hodAppeals.length} pending
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                Teacher-approved appeals from your department. Your approval updates the student's attendance to present.
              </p>
            </div>
            <AppealTable appeals={hodAppeals} stage="hod" onDecide={openDecide} showTeacherNote
              emptyText="No appeals awaiting HOD approval." />
          </div>
        )}
      </div>

      <Dialog open={!!decision} onOpenChange={() => setDecision(null)}>
        <DialogContent className="bg-white rounded-md" data-testid="appeal-decide-dialog">
          <DialogHeader>
            <DialogTitle className="capitalize">
              {decision?.action} appeal — {decision?.appeal.student_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500 font-data">
              {decision?.appeal.date} · {decision?.appeal.period_label} · {decision?.appeal.subject_name}
            </p>
            <div className="bg-slate-50 border border-slate-200 rounded-md px-4 py-3">
              <p className="text-xs text-slate-400 mb-1">Student's reason</p>
              <p className="text-sm text-slate-800">{decision?.appeal.reason}</p>
            </div>
            {decision?.action === "approve" && decision?.stage === "hod" && (
              <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                Approving will mark this student present for this class.
              </p>
            )}
            <div className="space-y-1.5">
              <p className="text-sm text-slate-500">Your reason (required)</p>
              <Textarea rows={3} data-testid="appeal-decide-reason-input"
                placeholder="Why are you approving or rejecting this appeal?"
                value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
            <Button onClick={submitDecision} disabled={saving} data-testid="appeal-decide-submit-btn"
              className={`w-full text-white ${decision?.action === "approve"
                ? "bg-emerald-700 hover:bg-emerald-800" : "bg-red-700 hover:bg-red-800"}`}>
              {saving ? "Saving…" : `Confirm ${decision?.action}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
